<script lang="ts">
	import type { Snippet } from 'svelte'
	import type { HTMLDialogAttributes } from 'svelte/elements'

	interface Props extends Omit<HTMLDialogAttributes, 'open'> {
		children: Snippet
		// Default is 522px for most Dialog confirmation modals, taken from styleguide.
		maxWidth?: string
	}

	let { children, class: className, maxWidth = '522px', style, ...rest }: Props = $props()

	let dialog: HTMLDialogElement

	export function open() {
		dialog.showModal()
	}

	export function close() {
		dialog.close()
	}
</script>

<dialog bind:this={dialog} class="app-dialog" {...rest} style="--dialog-max-width: {maxWidth};">
	<div class={['dialog-content', className]} {style}>
		{@render children()}
	</div>
</dialog>

<style>
	.app-dialog {
		/* Min. value is given from styleguide minimum display resolution 360px. */
		width: clamp(calc(360px - 32px), var(--dialog-max-width), calc(100vw - 32px));
		border: none;
		background: transparent;

		padding: 0;

		/* 60px navbar + 40px margin */
		margin: 100px auto auto;

		/* Closed state (also the target of the closing transition) */
		opacity: 0;
		transform: translateY(-100px);
		transition:
			opacity 0.3s ease-out,
			transform 0.3s ease-out,
			display 0.3s ease-out allow-discrete,
			overlay 0.3s ease-out allow-discrete;
	}

	.app-dialog[open] {
		display: block;
		opacity: 1;
		transform: translateY(0);
	}

	.dialog-content {
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: 20px;
		border-radius: 6px;
		background: #fff;
		box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
	}

	/* Entry animation: the state the dialog animates *from* when first shown */
	@starting-style {
		.app-dialog[open] {
			opacity: 0;
			transform: translateY(-100px);
		}
	}

	.app-dialog::backdrop {
		background: rgba(0, 0, 0, 0.25);

		/* Closed state (also the target of the closing transition) */
		opacity: 0;
		transition:
			opacity 0.3s ease-out,
			display 0.3s ease-out allow-discrete,
			overlay 0.3s ease-out allow-discrete;
	}

	.app-dialog[open]::backdrop {
		opacity: 1;
	}

	@starting-style {
		.app-dialog[open]::backdrop {
			opacity: 0;
		}
	}
</style>
